import { Box, MenuItem, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { VoicePresetSelect } from './VoicePresetSelect';
import { useVoiceSettingsStyles } from './styles';
import {
  BASIC_PRESETS,
  LANGUAGE_CODE_PROVIDERS,
  LANGUAGE_LABELS,
  VOICE_ID_REQUIRED_PROVIDERS,
  VOICE_ID_UNUSED_PROVIDERS,
  VOICE_PROVIDER_PRESETS,
} from './voiceSettings.presets';
import type { VoiceFormValues } from './types';

type BasicSettingKey = keyof Pick<VoiceFormValues, 'model' | 'language'>;

const includes = (list: readonly string[], value: string) => list.some((item) => item === value);

interface Props {
  provider: string;
  providerOptions: readonly string[];
  voiceId: VoiceFormValues['voiceId'];
  model: VoiceFormValues['model'];
  language: VoiceFormValues['language'];
  disabled: boolean;
  /** Shown above the fields when they are locked, e.g. by invalid JSON. */
  lockedReason?: string;
  providerError?: string;
  voiceIdError?: string;
  onProviderChange: (value: string) => void;
  onVoiceIdChange: (value: string) => void;
  onSettingChange: (key: BasicSettingKey, value: VoiceFormValues[BasicSettingKey]) => void;
}

export const VoiceBasicSettings = ({
  provider,
  providerOptions,
  voiceId,
  model,
  language,
  disabled,
  lockedReason,
  providerError,
  voiceIdError,
  onProviderChange,
  onVoiceIdChange,
  onSettingChange,
}: Props) => {
  const { t } = useTranslation();
  const { classes } = useVoiceSettingsStyles();
  const known = includes(VOICE_PROVIDER_PRESETS, provider);
  const languageCode = includes(LANGUAGE_CODE_PROVIDERS, provider);
  const voiceIdRequired = includes(VOICE_ID_REQUIRED_PROVIDERS, provider);
  const presets = known ? BASIC_PRESETS[provider as keyof typeof BASIC_PRESETS] : undefined;
  const voiceChoices = presets?.voiceId ?? [];
  const options = (key: BasicSettingKey): readonly string[] => presets?.[key] ?? [];
  // A known provider without choices does not use the field, so it is hidden.
  // A hidden field keeps its saved value.
  const shown = (key: BasicSettingKey) => !known || options(key).length > 0;
  const showVoiceId = !includes(VOICE_ID_UNUSED_PROVIDERS, provider);
  const select = (key: BasicSettingKey, value: VoiceFormValues[BasicSettingKey]) => (
    <VoicePresetSelect
      id={`voice-basic-${key}`}
      label={t(`superAdminManageVoice.advanced.fields.${key}`)}
      // A saved empty string means "not set", not a custom value.
      value={value || undefined}
      options={options(key)}
      disabled={disabled}
      unsetLabel={
        provider === '11labs' && key === 'model'
          ? t('superAdminManageVoice.advanced.automatic')
          : undefined
      }
      fallbackHint={
        provider === '11labs' && key === 'model'
          ? t('superAdminManageVoice.basic.automaticModelHint')
          : undefined
      }
      optionLabel={key === 'language' ? (option) => LANGUAGE_LABELS[option] ?? option : undefined}
      customHint={t('superAdminManageVoice.basic.customValue')}
      onChange={(next) => onSettingChange(key, next ?? null)}
    />
  );
  return (
    <>
      {lockedReason && (
        <Typography variant="caption" color="error" role="alert" component="p">
          {lockedReason}
        </Typography>
      )}
      <Box className={classes.fieldsGrid}>
        <TextField
          margin="dense"
          label={t('superAdminManageVoice.form.provider')}
          fullWidth
          select
          required
          value={provider}
          disabled={disabled}
          error={Boolean(providerError)}
          helperText={providerError}
          onChange={(event) => onProviderChange(event.target.value)}>
          {providerOptions.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
        {showVoiceId &&
          (voiceChoices.length > 0 ? (
            <VoicePresetSelect
              id="voice-basic-voiceId"
              label={t('superAdminManageVoice.form.voiceId')}
              value={voiceId || undefined}
              options={voiceChoices}
              disabled={disabled}
              required={voiceIdRequired}
              error={voiceIdError}
              fallbackHint={voiceIdRequired ? '' : undefined}
              customHint={t('superAdminManageVoice.basic.customValue')}
              onChange={(next) => onVoiceIdChange(next ?? '')}
            />
          ) : (
            <TextField
              margin="dense"
              label={t('superAdminManageVoice.form.voiceId')}
              fullWidth
              required={voiceIdRequired}
              value={voiceId ?? ''}
              disabled={disabled}
              error={Boolean(voiceIdError)}
              helperText={
                voiceIdError ??
                (provider === 'azure' ? t('superAdminManageVoice.basic.azureVoice') : undefined)
              }
              onChange={(event) => onVoiceIdChange(event.target.value)}
            />
          ))}
        {shown('model') && select('model', model)}
        {languageCode ? (
          <TextField
            id="voice-basic-language"
            margin="dense"
            label={t('superAdminManageVoice.advanced.fields.language')}
            fullWidth
            value={language ?? ''}
            disabled={disabled}
            helperText={t('superAdminManageVoice.basic.languageCodeHint')}
            onChange={(event) => onSettingChange('language', event.target.value || null)}
          />
        ) : (
          shown('language') && select('language', language)
        )}
      </Box>
    </>
  );
};
